/**
 * Collapses duplicate driver-notification rows produced by multiple Admin
 * `notification_auto_rules` bound to the same trigger event.
 *
 * Two rows collapse when they share the same recipient, canonical `type`,
 * optional booking scope, and fall inside the same 60-second bucket. The
 * first row (as sorted by the caller) wins; the rest are dropped from the
 * display list.
 */

export interface DedupableNotification {
  id: string;
  type?: string | null;
  created_at: string;
  recipient_id?: string | null;
  dedup_key?: string | null;
  // Optional booking scoping fields — present on lifecycle rows.
  booking_number?: string | null;
  booking_id?: string | null;
  message?: string | null;
}

const BUCKET_MS = 60_000;

export function dedupNotificationKey(n: DedupableNotification): string {
  const type = String(n.type || '').trim() || 'unknown';
  const recipient = String(n.recipient_id || '');
  const bookingRef =
    (n.booking_number && String(n.booking_number)) ||
    (n.booking_id && String(n.booking_id)) ||
    // Fall back to extracting a booking id from the dedup_key when the row
    // does not carry a dedicated booking column (older schema).
    extractBookingFromDedupKey(n.dedup_key) ||
    '';
  const bucket = Math.floor(new Date(n.created_at).getTime() / BUCKET_MS);
  return `${recipient}|${type}|${bookingRef}|${bucket}`;
}

function extractBookingFromDedupKey(dedup?: string | null): string {
  if (!dedup) return '';
  const m = /booking:([0-9a-f-]{8,})/i.exec(dedup);
  return m ? m[1] : '';
}

export function dedupNotifications<T extends DedupableNotification>(rows: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const row of rows) {
    const key = dedupNotificationKey(row);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}
